import { sanitizeProjectFolderName } from "@/lib/project-utils";

export type PortProxyInfo = {
  domain?: string | null;
  slugs?: string[] | null;
  defaultPort?: number | null;
  ports?: number[] | null;
};

export type PortProxyMetadata = Omit<PortProxyInfo, "ports">;

export type ResolvedPreviewState = {
  domain: string;
  slugs: string[];
  defaultPort: number;
  ports?: number[];
};

const GENERIC_FOLDER_NAMES = new Set(["home", "sprite", "user"]);

function normalizeLivePorts(ports: number[] | null | undefined): number[] | undefined {
  if (!Array.isArray(ports)) return undefined;

  const uniquePorts = [...new Set(
    ports.filter((port): port is number => Number.isInteger(port) && port > 0),
  )].sort((a, b) => a - b);

  return uniquePorts.length > 0 ? uniquePorts : undefined;
}

export function mergePreviewPortProxyInfo(
  metadata: PortProxyMetadata | null | undefined,
  livePorts: number[] | null | undefined,
): PortProxyInfo | null {
  if (!metadata) return null;
  return {
    ...metadata,
    ports: normalizeLivePorts(livePorts),
  };
}

function deriveSlugFromWorkingDirectory(workingDirectory: string | null | undefined): string | null {
  if (!workingDirectory) return null;

  const basename = workingDirectory.replace(/\/+$/, "").split("/").pop()?.trim() || "";
  if (!basename || GENERIC_FOLDER_NAMES.has(basename.toLowerCase())) {
    return null;
  }

  return sanitizeProjectFolderName(basename);
}

export function buildPreviewState(
  portProxyInfo: PortProxyInfo | null | undefined,
  workingDirectory: string | null | undefined,
): ResolvedPreviewState | null {
  if (!portProxyInfo?.domain) return null;

  const slugs = (portProxyInfo.slugs ?? []).filter((slug): slug is string => Boolean(slug?.trim()));
  if (slugs.length > 0) {
    return {
      domain: portProxyInfo.domain,
      slugs,
      defaultPort: portProxyInfo.defaultPort ?? 3000,
      ports: portProxyInfo.ports ?? undefined,
    };
  }

  const fallbackSlug = deriveSlugFromWorkingDirectory(workingDirectory);
  if (!fallbackSlug) return null;

  return {
    domain: portProxyInfo.domain,
    slugs: [fallbackSlug],
    defaultPort: portProxyInfo.defaultPort ?? 3000,
    ports: portProxyInfo.ports ?? undefined,
  };
}
